import React, { useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import * as XLSX from "xlsx";
import axiosInstance from "../../../services/axios.instance";
import { API_URL } from "../../../services/apiConfig";
import { useState } from "react";
import { AflApiResponse, ExportList } from "../../../types";
import { Form as BootstrapForm, Dropdown } from "react-bootstrap";

import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormControl,
  Row,
} from "react-bootstrap";
import { FormatListBulleted } from "@mui/icons-material";
import ConfirmationModal from "./../../../components/ConfirmationModal";
import LoaderExportFileModal from "./../../../components/LoaderExportFileModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const ExportFile = () => {
  const navigate = useNavigate();
  const [serviceList, setServiceList] = useState<AflApiResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(0);

  const [searchResults, setSearchResults] = useState<AflApiResponse | null>(
    null
  );

  const [searchCriteria, setSearchCriteria] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [allSearchResults, setAllSearchResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([
    "ReferenceNo",
    "JobOpenDate",
    "Status",
    "CustomerName",
    "PlateNo",
    "ChasisNo",
    "MobileNo",
    "From Location",
    "To Location",
    "Service",
    "VehicleMake",
    "VehicleModel",
    "Comments",
    "CreatedDateTime",
    "AcceptedDateTime",
    // "StartDateTime",
    "DriverName",
    "ReachedDateTime",
    "ServiceStartDate",
    "CompletedDateTime",
    "DriverContact",
    "ReachAndCancel",
  ]);

  const allColumns = [
    { key: "ReferenceNo", label: "Reference No" },
    { key: "JobOpenDate", label: "Job Open Date" },
    { key: "Status", label: "Status" },
    { key: "CustomerName", label: "Customer Name" },
    { key: "PlateNo", label: "Plate No" },
    { key: "ChasisNo", label: "Chassis No" },
    { key: "MobileNo", label: "Mobile No" },
    { key: "From Location", label: "From Location" },
    { key: "To Location", label: "To Location" },
    { key: "Service", label: "Service" },
    { key: "VehicleMake", label: "Vehicle Make" },
    { key: "VehicleModel", label: "Vehicle Model" },
    { key: "Comments", label: "Comments" },
    { key: "CreatedDateTime", label: "Created Date Time" },
    { key: "AcceptedDateTime", label: "Accepted Date Time" },
    // { key: "StartDateTime", label: "Start Date Time" },
    { key: "DriverName", label: "Driver Name" },
    { key: "ReachedDateTime", label: "Reached Date Time" },
    { key: "ServiceStartDate", label: "Service Start Date" },
    { key: "CompletedDateTime", label: "Completed Date Time" },
    { key: "DriverContact", label: "Driver Contact" },
    { key: "ReachAndCancel", label: "Reach And Cancel" },
  ];

  const handleColumnToggle = (columnKey: string) => {
    setSelectedColumns((prev) =>
      prev.includes(columnKey)
        ? prev.filter((col) => col !== columnKey)
        : [...prev, columnKey]
    );
  };

  const isColumnSelected = (columnKey: string) => {
    return selectedColumns.includes(columnKey);
  };

  // Helper function to get status text
  const getStatusText = (serviceOrder: any) => {
    const state = serviceOrder?.aaaServiceRequestModel?.state;
    const isDriverReachedAndCancelled =
      serviceOrder?.orderServiceDetailsModel?.isDriverReachedAndCancelled;
    const isAFLUser = true; // Assuming export is for AFL users

    // Handle AFL user cases first
    if (isAFLUser && state === "driver_cancel") {
      return isDriverReachedAndCancelled ? "R&C" : "Cancelled";
    }

    // Handle reached and cancelled cases for non-AFL users
    if (isDriverReachedAndCancelled) {
      if (state === "cancel") {
        return "R&C";
      }
      if (state === "driver_cancel" && !isAFLUser) {
        return "Driver Cancel Pending";
      }
    }

    // For AFL users, show 'Cancelled' for cancelled states
    if (isAFLUser && (state === "driver_cancel" || state === "cancel")) {
      return "Cancelled";
    }

    // For non-AFL users, show 'Driver Cancel Pending' when state is 'driver_cancel' and not reached and cancelled
    if (
      !isAFLUser &&
      state === "driver_cancel" &&
      !isDriverReachedAndCancelled
    ) {
      return "Driver Cancel Pending";
    }

    switch (state) {
      case "completed_by_driver":
        return "Completed";
      case "reached_and_cancelled":
        return "R&C";
      case "driver_cancel":
        return "Driver Cancel";
      default:
        return state || "N/A";
    }
  };

  // Export functionality
  const handleExport = () => {
    if (!allSearchResults || allSearchResults.length === 0) return;

    // Create worksheet data with only selected columns
    const wsData = allSearchResults.map((order, index) => {
      // Calculate all possible fields first
      const createdAtFormatted =
        formatDate(order?.orderServiceDetailsModel?.scheduledFor) || "N/A";
      const acceptedDateTime =
        order?.orderView?.order_status_history_responses?.some(
          (status: any) =>
            status.delivery_partner_status === "DELIVERY_ACCEPTED"
        )
          ? formatDate(
            order?.orderView?.order_status_history_responses?.find(
              (status: any) =>
                status.delivery_partner_status === "DELIVERY_ACCEPTED"
            )?.status_changed_at
          )
          : "N/A";
      const reachedDateTime =
        order?.orderView?.order_status_history_responses?.some(
          (status: any) =>
            status.delivery_partner_status === "REACHED_CUSTOMER_LOCATION"
        )
          ? formatDate(
            order?.orderView?.order_status_history_responses?.find(
              (status: any) =>
                status.delivery_partner_status === "REACHED_CUSTOMER_LOCATION"
            )?.status_changed_at
          )
          : "N/A";
      const isDistanceBased =
        order?.orderServiceDetailsModel?.serviceType === "DISTANCE_BASED";
      const serviceStartDate = isDistanceBased
        ? order?.orderView?.order_status_history_responses?.find(
          (status: any) =>
            status.delivery_partner_status === "VEHICLE_COLLECTED"
        )?.status_changed_at
          ? formatDate(
            order.orderView.order_status_history_responses.find(
              (status: any) =>
                status.delivery_partner_status === "VEHICLE_COLLECTED"
            ).status_changed_at
          )
          : "N/A"
        : order?.orderView?.order_status_history_responses?.find(
          (status: any) =>
            status.delivery_partner_status === "SERVICE_STARTED"
        )?.status_changed_at
          ? formatDate(
            order.orderView.order_status_history_responses.find(
              (status: any) =>
                status.delivery_partner_status === "SERVICE_STARTED"
            ).status_changed_at
          )
          : "N/A";
      const completedDateTime =
        order?.orderView?.order_status_history_responses?.some(
          (status: any) => status.delivery_partner_status === "COMPLETED"
        )
          ? formatDate(
            order?.orderView?.order_status_history_responses?.find(
              (status: any) => status.delivery_partner_status === "COMPLETED"
            )?.status_changed_at
          )
          : "N/A";
      // const startDates = order?.aaaServiceRequestModel?.comments?.some(
      //   (comment: { commentStatus: string }) =>
      //     comment.commentStatus === "dispatch"
      // )
      //   ? formatDate(
      //       order?.aaaServiceRequestModel?.comments?.find(
      //         (comment: { commentStatus: string }) =>
      //           comment.commentStatus === "dispatch"
      //       )?.createDate
      //     )
      //   : "N/A";

      // Create a base object with all possible fields
      const allFields: Record<string, any> = {
        "S.No": index + 1,
        ReferenceNo: order?.orderServiceDetailsModel?.referenceNumber || "N/A",
        JobOpenDate: formatDate(order?.orderView?.createdAt) || "N/A",
        Status: getStatusText(order),
        CustomerName: order?.orderView?.customer_name || "N/A",
        PlateNo: order?.orderView?.plate_number || "N/A",
        ChasisNo: order?.orderView?.chassis_number || "N/A",
        MobileNo: order?.orderView?.customer_phone_number || "N/A",
        "From Location": order?.orderView?.delivery_location?.address || "N/A",
        "To Location":
          order?.orderView?.recollection_location?.address || "N/A",
        Service: order?.orderServiceDetailsModel?.serviceName || "N/A",
        VehicleMake:
          order?.orderServiceDetailsModel?.vehicleMake ||
          order?.aaaServiceRequestModel?.vehicleMake ||
          "N/A",
        VehicleModel: order?.aaaServiceRequestModel?.vehicleModel || "N/A",
        Comments: order?.orderServiceDetailsModel?.aflComments || "N/A",
        CreatedDateTime: createdAtFormatted,
        AcceptedDateTime: acceptedDateTime,
        // StartDateTime: startDates,
        DriverName: order?.orderView?.delivery_boy_details?.firstName || "N/A",
        ReachedDateTime: reachedDateTime,
        ServiceStartDate: serviceStartDate,
        CompletedDateTime: completedDateTime,
        DriverContact:
          order?.orderView?.delivery_boy_details?.phoneNumber || "N/A",
        ReachAndCancel:
          order?.orderServiceDetailsModel?.isDriverReachedAndCancelled,
      };

      // Filter the fields based on selected columns
      const filteredFields: Record<string, any> = {};

      // Always include S.No
      filteredFields["S.No"] = allFields["S.No"];

      // Include other selected columns
      selectedColumns.forEach((column) => {
        if (allFields.hasOwnProperty(column)) {
          filteredFields[column] = allFields[column];
        }
      });

      return filteredFields;
    });

    // Create worksheet with the filtered data
    const ws = XLSX.utils.json_to_sheet(wsData);

    // Create workbook and add worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Service List");

    // Generate filename with current date/time
    const currentDate = new Date().toISOString().split("T")[0];
    const currentTime = new Date()
      .toLocaleTimeString("en-US", { hour12: false })
      .replace(/:/g, "_");
    const filename = `ServiceList_${currentDate}_${currentTime}.xlsx`;

    // Export to Excel
    XLSX.writeFile(wb, filename);
  };

  // Reset search query and results when changing search criteria
  const handleSearchCriteriaChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSearchCriteria(e.target.value);
    setSearchQuery("");
    setStartDate(null);
    setEndDate(null);
    setSearchResults(null);
    setHasSearched(false);
  };
  const accessToken = localStorage.getItem("access_token");

  const generatePageNumbers = (currentPage: number, totalPages: number) => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }

    if (currentPage <= 3) {
      return [0, 1, 2, 3, -1, totalPages - 2, totalPages - 1];
    }

    if (currentPage >= totalPages - 4) {
      return [
        0,
        1,
        -1,
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
      ];
    }

    return [
      0,
      1,
      -1,
      currentPage - 1,
      currentPage,
      currentPage + 1,
      -1,
      totalPages - 1,
    ];
  };

  useEffect(() => {
    if (hasSearched) {
      setServiceList(null);
    }
  }, [hasSearched]);

  //-------------------------------

  const handleSearch = async () => {
    const payload: any = {
      reference_number: searchCriteria === "ref_num" ? searchQuery : "",
      status: searchCriteria === "status" ? searchQuery : "",
      service_type: searchCriteria === "service_type" ? searchQuery : "",
      phone_number: searchCriteria === "phone_number" ? searchQuery : "",
    };

    if (searchCriteria === "status" && searchQuery) {
      if (startDate) {
        payload.start_date = startDate.toLocaleDateString("en-CA");
      }
      if (endDate) {
        payload.end_date = endDate.toLocaleDateString("en-CA");
      }
    } else if (searchCriteria === "date") {
      if (startDate) {
        payload.start_date = startDate.toLocaleDateString("en-CA");
      }
      if (endDate) {
        payload.end_date = endDate.toLocaleDateString("en-CA");
      }
    }

    try {
      setIsLoading(true);
      let page = 0;
      let allData: any[] = [];
      let keepFetching = true;

      while (keepFetching) {
        const response = await axiosInstance.post(
          `${API_URL.SERVICE_LIST}?size=50&page=${page}`,
          payload
        );

        const pageData = response?.data?.content || [];

        if (pageData.length === 0) {
          keepFetching = false;
        } else {
          allData = [...allData, ...pageData];
          page++;
        }
      }

      setAllSearchResults(allData);
      setHasSearched(true);
    } catch (error) {
      console.error("Error fetching search results", error);
      setHasSearched(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Memoize the page numbers generation
  const memoizedGeneratePageNumbers = useCallback(
    (currentPage: number, totalPages: number) => {
      if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i);
      }

      if (currentPage <= 3) {
        return [0, 1, 2, 3, -1, totalPages - 2, totalPages - 1];
      }

      if (currentPage >= totalPages - 4) {
        return [
          0,
          1,
          -1,
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
        ];
      }

      return [
        0,
        1,
        -1,
        currentPage - 1,
        currentPage,
        currentPage + 1,
        -1,
        totalPages - 1,
      ];
    },
    []
  );

  const [searchVisiblePages, setSearchVisiblePages] = useState<number[]>(() => {
    const totalPages = searchResults?.totalPages || 0;
    return generatePageNumbers(0, totalPages);
  });

  const handlePageSearchChange = (page: number) => {
    handleSearch(); // handleSearch no longer needs page parameter
  };

  //------------------------------------------------------------------
  const [showAcceptConfirmationModal, setShowAcceptConfirmationModal] =
    useState(false);

  const [acceptConfirmationMessage, setAcceptConfirmationMessage] =
    useState("");
  const [selectedOrderId, setSelectedOrderId] = useState<string>("");

  //-------------------------------------------------------------------
  const formatDate = (isoDate: any) => {
    const date = new Date(isoDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const location = useLocation();
  const pathname = location.pathname;
  const ordersToDisplay = useMemo(() => {
    if (hasSearched && allSearchResults.length > 0) return allSearchResults;
    if (!hasSearched && serviceList?.content) return serviceList.content;
    return [];
  }, [allSearchResults, serviceList, hasSearched]);

  return (
    <div className="row">
      <Card className="card-bg-afl text-start">
        <CardBody>
          <h4 className="text-start car_upload_text align-items-center d-flex">
            <FormatListBulleted
              style={{ color: "#ed7d31", fontSize: "35px" }}
            />
            Export Data by Search
          </h4>
          <div className="col-md-12 mb-3 mt-2">
            <Form.Group as={Row} className="align-items-center gx-2">
              <Col sm="3">
                <Form.Select
                  className="select-invoice-bg-black-edit dashboard-poppins-font-style custom-input-afl"
                  aria-label="Select search criteria"
                  value={searchCriteria}
                  onChange={handleSearchCriteriaChange}
                >
                  <option value="">Search by</option>
                  <option value="ref_num">Reference Number</option>
                  <option value="service_type">Service Type</option>
                  <option value="status">Status</option>
                  <option value="phone_number">Phone Number</option>
                  <option value="date">Date</option>
                </Form.Select>
              </Col>

              {searchCriteria && (
                <>
                  <Col sm="3">
                    {searchCriteria === "status" ? (
                      <>
                        <Form.Select
                          className="merchant-input-style-create-product custom-input-afl"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        >
                          <option value="">Select Status</option>
                          <option value="initiate">Initiate</option>
                          <option value="dispatch">Dispatch</option>
                          <option value="start">Start</option>
                          <option value="reach">Reach</option>
                          <option value="completed_by_driver">Completed</option>
                          <option value="done">Done</option>
                          <option value="cancel">Cancelled</option>
                          <option value="reached_and_cancelled">
                            Reached and Cancelled
                          </option>
                        </Form.Select>

                        {searchQuery && (
                          <div className="d-flex gap-2 align-items-center mt-2">
                            <DatePicker
                              selected={startDate}
                              onChange={(date) => setStartDate(date)}
                              selectsStart
                              startDate={startDate}
                              endDate={endDate}
                              maxDate={endDate || new Date()}
                              className="form-control merchant-input-style-create-product custom-input-afl"
                              dateFormat="dd-MM-yyyy"
                              placeholderText="DD-MM-YYYY"
                              showYearDropdown
                              scrollableYearDropdown
                              yearDropdownItemNumber={15}
                              customInput={
                                <FormControl
                                  value={
                                    startDate
                                      ? `${String(startDate.getDate()).padStart(
                                        2,
                                        "0"
                                      )}-${String(
                                        startDate.getMonth() + 1
                                      ).padStart(
                                        2,
                                        "0"
                                      )}-${startDate.getFullYear()}`
                                      : ""
                                  }
                                  readOnly
                                  className="merchant-input-style-create-product custom-input-afl"
                                />
                              }
                            />
                            <span>to</span>
                            <DatePicker
                              selected={endDate}
                              onChange={(date) => setEndDate(date)}
                              selectsEnd
                              startDate={startDate}
                              endDate={endDate}
                              minDate={startDate}
                              maxDate={new Date()}
                              className="form-control merchant-input-style-create-product custom-input-afl"
                              dateFormat="dd-MM-yyyy"
                              placeholderText="DD-MM-YYYY"
                              showYearDropdown
                              scrollableYearDropdown
                              yearDropdownItemNumber={15}
                              customInput={
                                <FormControl
                                  value={
                                    endDate
                                      ? `${String(endDate.getDate()).padStart(
                                        2,
                                        "0"
                                      )}-${String(
                                        endDate.getMonth() + 1
                                      ).padStart(
                                        2,
                                        "0"
                                      )}-${endDate.getFullYear()}`
                                      : ""
                                  }
                                  readOnly
                                  className="merchant-input-style-create-product custom-input-afl"
                                />
                              }
                            />
                          </div>
                        )}
                      </>
                    ) : searchCriteria === "date" ? (
                      <div className="d-flex gap-2 align-items-center">
                        <DatePicker
                          selected={startDate}
                          onChange={(date) => setStartDate(date)}
                          selectsStart
                          startDate={startDate}
                          endDate={endDate}
                          maxDate={endDate || new Date()}
                          className="form-control merchant-input-style-create-product custom-input-afl"
                          dateFormat="dd-MM-yyyy"
                          placeholderText="DD-MM-YYYY"
                          showYearDropdown
                          scrollableYearDropdown
                          yearDropdownItemNumber={15}
                          customInput={
                            <FormControl
                              value={
                                startDate
                                  ? `${String(startDate.getDate()).padStart(
                                    2,
                                    "0"
                                  )}-${String(
                                    startDate.getMonth() + 1
                                  ).padStart(
                                    2,
                                    "0"
                                  )}-${startDate.getFullYear()}`
                                  : ""
                              }
                              readOnly
                              className="merchant-input-style-create-product custom-input-afl"
                            />
                          }
                        />
                        <span>to</span>
                        <DatePicker
                          selected={endDate}
                          onChange={(date) => setEndDate(date)}
                          selectsEnd
                          startDate={startDate}
                          endDate={endDate}
                          minDate={startDate}
                          maxDate={new Date()}
                          className="form-control merchant-input-style-create-product custom-input-afl"
                          dateFormat="dd-MM-yyyy"
                          placeholderText="DD-MM-YYYY"
                          showYearDropdown
                          scrollableYearDropdown
                          yearDropdownItemNumber={15}
                          customInput={
                            <FormControl
                              value={
                                endDate
                                  ? `${String(endDate.getDate()).padStart(
                                    2,
                                    "0"
                                  )}-${String(
                                    endDate.getMonth() + 1
                                  ).padStart(
                                    2,
                                    "0"
                                  )}-${endDate.getFullYear()}`
                                  : ""
                              }
                              readOnly
                              className="merchant-input-style-create-product custom-input-afl"
                            />
                          }
                        />
                      </div>
                    ) : (
                      <FormControl
                        className="merchant-input-style-create-product custom-input-afl"
                        aria-label="Text input with select"
                        placeholder="Enter your Search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    )}
                  </Col>

                  <Col sm="2">
                    <Button
                      className="btn all_button_style text-black my-1"
                      onClick={async () => {
                        await handleSearch();
                      }}
                      disabled={
                        searchCriteria === "status"
                          ? !searchQuery || !startDate || !endDate
                          : searchCriteria === "date"
                            ? !startDate || !endDate
                            : !searchQuery
                      }
                    >
                      Search
                    </Button>
                  </Col>

                  <Col sm="2">
                    <Button
                      className="btn all_button_style text-black my-1"
                      onClick={handleExport}
                      disabled={!hasSearched || isLoading}
                    >
                      Export
                    </Button>
                  </Col>

                  <Col sm="1">
                    <Dropdown>
                      <Dropdown.Toggle
                        className="btn all_button_style text-black my-1"
                        id="column-selector"
                      >
                        Fields To Export
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        style={{ padding: "10px", minWidth: "250px" }}
                      >
                        <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                          {allColumns.map((column) => (
                            <div
                              key={column.key}
                              className="form-check py-1 border-bottom"
                            >
                              <input
                                className="afl-input"
                                type="checkbox"
                                id={`column-${column.key}`}
                                checked={isColumnSelected(column.key)}
                                onChange={() => handleColumnToggle(column.key)}
                              // style={{
                              //   backgroundColor: isColumnSelected(column.key)
                              //     ? "#F2B651"
                              //     : "",
                              //   borderColor: isColumnSelected(column.key)
                              //     ? "#F2B651"
                              //     : "",
                              // }}
                              />
                              <label
                                className="form-check-label px-1"
                                htmlFor={`column-${column.key}`}
                                style={{ cursor: "pointer" }}
                              >
                                {column.label}
                              </label>
                            </div>
                          ))}
                        </div>
                        <div className="d-grid gap-2 mt-2">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() =>
                              setSelectedColumns(
                                allColumns.map((col) => col.key)
                              )
                            }
                          >
                            Select All
                          </button>
                        </div>
                      </Dropdown.Menu>
                    </Dropdown>
                  </Col>
                </>
              )}
            </Form.Group>
          </div>
          <div className="col-md-12">
            {/* Loading Modal */}
            <LoaderExportFileModal show={isLoading} />
          </div>
          <div className="col-md-12">
            <div className="row pt-3">
              <div className="col-12 dashboard-table-div">
                <table className="table table-hover dashboard-table-afl table-afl">
                  {hasSearched && ordersToDisplay?.length === 0 && (
                    <tbody>
                      <tr>
                        <td colSpan={10} className="text-center py-4">
                          No Search results found
                        </td>
                      </tr>
                    </tbody>
                  )}
                  {hasSearched && ordersToDisplay?.length > 0 && (
                    <>
                      <thead>
                        <tr>
                          <th>#</th>
                          {selectedColumns.includes("ReferenceNo") && (
                            <th>Reference No</th>
                          )}
                          {selectedColumns.includes("JobOpenDate") && (
                            <th>Job Open Date</th>
                          )}
                          {selectedColumns.includes("Status") && (
                            <th>Status</th>
                          )}
                          {selectedColumns.includes("CustomerName") && (
                            <th>Customer Name</th>
                          )}
                          {selectedColumns.includes("PlateNo") && (
                            <th>Plate No</th>
                          )}
                          {selectedColumns.includes("ChasisNo") && (
                            <th>Chassis No</th>
                          )}
                          {selectedColumns.includes("MobileNo") && (
                            <th>Mobile No</th>
                          )}
                          {selectedColumns.includes("From Location") && (
                            <th>From Location</th>
                          )}
                          {selectedColumns.includes("To Location") && (
                            <th>To Location</th>
                          )}
                          {selectedColumns.includes("Service") && (
                            <th>Service</th>
                          )}
                          {selectedColumns.includes("VehicleMake") && (
                            <th>Vehicle Make</th>
                          )}
                          {selectedColumns.includes("VehicleModel") && (
                            <th>Vehicle Model</th>
                          )}
                          {selectedColumns.includes("Comments") && (
                            <th>Comments</th>
                          )}
                          {selectedColumns.includes("CreatedDateTime") && (
                            <th>Created Date Time</th>
                          )}
                          {selectedColumns.includes("AcceptedDateTime") && (
                            <th>Accepted Date Time</th>
                          )}
                          {/* {selectedColumns.includes("StartDateTime") && (
                            <th>Start Date Time</th>
                          )} */}
                          {selectedColumns.includes("DriverName") && (
                            <th>Driver Name</th>
                          )}
                          {selectedColumns.includes("ReachedDateTime") && (
                            <th>Reached Date Time</th>
                          )}
                          {selectedColumns.includes("ServiceStartDate") && (
                            <th>Service Start Date</th>
                          )}
                          {selectedColumns.includes("CompletedDateTime") && (
                            <th>Completed Date Time</th>
                          )}
                          {selectedColumns.includes("DriverContact") && (
                            <th>Driver Contact</th>
                          )}
                          {selectedColumns.includes("ReachAndCancel") && (
                            <th>Reach And Cancel</th>
                          )}
                        </tr>
                      </thead>

                      {/* table body */}
                      <tbody>
                        {ordersToDisplay.map(
                          (serviceOrder: any, index: number) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              {selectedColumns.includes("ReferenceNo") && (
                                <td>
                                  {serviceOrder?.orderServiceDetailsModel
                                    ?.referenceNumber || "N/A"}
                                </td>
                              )}
                              {selectedColumns.includes("JobOpenDate") && (
                                <td>
                                  {formatDate(
                                    serviceOrder?.orderView?.createdAt
                                  ) || "N/A"}
                                </td>
                              )}
                              {selectedColumns.includes("Status") && (
                                <td>{getStatusText(serviceOrder)}</td>
                              )}
                              {selectedColumns.includes("CustomerName") && (
                                <td>
                                  {serviceOrder?.orderView?.customer_name ||
                                    "N/A"}
                                </td>
                              )}
                              {selectedColumns.includes("PlateNo") && (
                                <td>
                                  {serviceOrder?.orderView?.plate_number ||
                                    "N/A"}
                                </td>
                              )}
                              {selectedColumns.includes("ChasisNo") && (
                                <td>
                                  {serviceOrder?.orderView?.chassis_number ||
                                    "N/A"}
                                </td>
                              )}
                              {selectedColumns.includes("MobileNo") && (
                                <td>
                                  {serviceOrder?.orderView
                                    ?.customer_phone_number || "N/A"}
                                </td>
                              )}
                              {selectedColumns.includes("From Location") && (
                                <td>
                                  {serviceOrder?.orderView?.delivery_location
                                    ?.address || "N/A"}
                                </td>
                              )}
                              {selectedColumns.includes("To Location") && (
                                <td>
                                  {serviceOrder?.orderView
                                    ?.recollection_location?.address || "N/A"}
                                </td>
                              )}
                              {selectedColumns.includes("Service") && (
                                <td>
                                  {serviceOrder?.orderServiceDetailsModel
                                    ?.serviceName || "N/A"}
                                </td>
                              )}
                              {selectedColumns.includes("VehicleMake") && (
                                <td>
                                  {serviceOrder?.orderServiceDetailsModel
                                    ?.vehicleMake ||
                                    serviceOrder?.aaaServiceRequestModel
                                      ?.vehicleMake ||
                                    "N/A"}
                                </td>
                              )}
                              {selectedColumns.includes("VehicleModel") && (
                                <td>
                                  {serviceOrder?.aaaServiceRequestModel
                                    ?.vehicleModel || "N/A"}
                                </td>
                              )}
                              {selectedColumns.includes("Comments") && (
                                <td>
                                  {serviceOrder?.orderServiceDetailsModel
                                    ?.aflComments || "N/A"}
                                </td>
                              )}
                              {selectedColumns.includes("CreatedDateTime") && (
                                <td>
                                  {formatDate(
                                    serviceOrder?.orderServiceDetailsModel
                                      ?.scheduledFor
                                  ) || "N/A"}
                                </td>
                              )}
                              {selectedColumns.includes("AcceptedDateTime") && (
                                <td>
                                  {serviceOrder?.orderView?.order_status_history_responses?.find(
                                    (status: any) =>
                                      status.delivery_partner_status ===
                                      "DELIVERY_ACCEPTED"
                                  )?.status_changed_at
                                    ? formatDate(
                                      serviceOrder.orderView.order_status_history_responses.find(
                                        (status: any) =>
                                          status.delivery_partner_status ===
                                          "DELIVERY_ACCEPTED"
                                      ).status_changed_at
                                    )
                                    : "N/A"}
                                </td>
                              )}
                              {/* {selectedColumns.includes("StartDateTime") && (
                                <td>
                                  {serviceOrder?.aaaServiceRequestModel?.comments?.find(
                                    (comment: { commentStatus: string }) =>
                                      comment.commentStatus === "dispatch"
                                  )?.createDate
                                    ? formatDate(
                                        serviceOrder.aaaServiceRequestModel.comments.find(
                                          (comment: {
                                            commentStatus: string;
                                          }) =>
                                            comment.commentStatus === "dispatch"
                                        ).createDate
                                      )
                                    : "N/A"}
                                </td>
                              )} */}
                              {selectedColumns.includes("DriverName") && (
                                <td>
                                  {serviceOrder?.orderView?.delivery_boy_details
                                    ?.firstName || "N/A"}
                                </td>
                              )}
                              {selectedColumns.includes("ReachedDateTime") && (
                                <td>
                                  {serviceOrder?.orderView?.order_status_history_responses?.find(
                                    (status: any) =>
                                      status.delivery_partner_status ===
                                      "REACHED_CUSTOMER_LOCATION"
                                  )?.status_changed_at
                                    ? formatDate(
                                      serviceOrder.orderView.order_status_history_responses.find(
                                        (status: any) =>
                                          status.delivery_partner_status ===
                                          "REACHED_CUSTOMER_LOCATION"
                                      ).status_changed_at
                                    )
                                    : "N/A"}
                                </td>
                              )}
                              {selectedColumns.includes("ServiceStartDate") && (
                                <td>
                                  {(() => {
                                    const isDistanceBased =
                                      serviceOrder?.orderServiceDetailsModel
                                        ?.serviceType === "DISTANCE_BASED";
                                    const status = isDistanceBased
                                      ? serviceOrder?.orderView?.order_status_history_responses?.find(
                                        (s: any) =>
                                          s.delivery_partner_status ===
                                          "VEHICLE_COLLECTED"
                                      )
                                      : serviceOrder?.orderView?.order_status_history_responses?.find(
                                        (s: any) =>
                                          s.delivery_partner_status ===
                                          "SERVICE_STARTED"
                                      );
                                    return status?.status_changed_at
                                      ? formatDate(status.status_changed_at)
                                      : "N/A";
                                  })()}
                                </td>
                              )}
                              {selectedColumns.includes(
                                "CompletedDateTime"
                              ) && (
                                  <td>
                                    {serviceOrder?.orderView?.order_status_history_responses?.find(
                                      (status: any) =>
                                        status.delivery_partner_status ===
                                        "COMPLETED"
                                    )?.status_changed_at
                                      ? formatDate(
                                        serviceOrder.orderView.order_status_history_responses.find(
                                          (status: any) =>
                                            status.delivery_partner_status ===
                                            "COMPLETED"
                                        ).status_changed_at
                                      )
                                      : "N/A"}
                                  </td>
                                )}
                              {selectedColumns.includes("DriverContact") && (
                                <td>
                                  {serviceOrder?.orderView?.delivery_boy_details
                                    ?.phoneNumber || "N/A"}
                                </td>
                              )}
                              {selectedColumns.includes("ReachAndCancel") && (
                                <td>
                                  {serviceOrder?.orderServiceDetailsModel
                                    ?.isDriverReachedAndCancelled
                                    ? "Yes"
                                    : "No"}
                                </td>
                              )}
                            </tr>
                          )
                        )}
                      </tbody>
                    </>
                  )}
                </table>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
      <ConfirmationModal
        isOpen={showAcceptConfirmationModal}
        onClose={() => setShowAcceptConfirmationModal(false)}
        onConfirm={() => {
          setShowAcceptConfirmationModal(false);
        }}
        message={acceptConfirmationMessage}
      />
    </div>
  );
};

export default ExportFile;
